<template>
  <div ref="containerRef" class="cad-viewer-aps-container" :style="containerStyle">
    <!--
      NOTE(CAD Phase 4):
      (a) 実描画には OUTPUT_DIST 配下に SVF バンドル一式(再帰抽出)が必要 = AWS 変換コンテナ依存。
          現時点では cad_ready=false のため is_cad_previewable=false でこのコンポーネントは表示されない。
      (b) 外部 CDN(developer.api.autodesk.com) の script/CSS 読み込みは既存 CSP(SecurityHeaders) で
          許可が要る可能性がある。活性化時に SecurityHeaders.php の script-src / connect-src /
          style-src に Autodesk ドメインを追加すること(本 Phase では CSP を変更しない・要確認事項)。
    -->
    <div
      ref="viewerEl"
      class="cad-viewer-aps-viewer"
      :style="viewerStyle"
    ></div>
    <div
      v-if="loading"
      class="d-flex justify-content-center align-items-center text-secondary"
      :style="overlayStyle"
    >
      <div class="text-center">
        <i class="fas fa-drafting-compass fa-2x mb-2 d-block" aria-hidden="true"></i>
        <div class="small">CADビューアを読み込んでいます</div>
      </div>
    </div>
    <div
      v-else-if="error"
      class="d-flex justify-content-center align-items-center text-danger text-center px-3"
      :style="overlayStyle"
    >
      <div>
        <i class="fas fa-exclamation-triangle mb-2 d-block fa-2x" aria-hidden="true"></i>
        <div>{{ error }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch, computed, nextTick } from 'vue';

// シングルトン Promise で APS Viewer SDK を多重ロードしない
let apsViewerPromise = null;

const loadApsViewer = () => {
  if (apsViewerPromise) {
    return apsViewerPromise;
  }
  apsViewerPromise = new Promise((resolve, reject) => {
    // CSS インジェクション
    if (!document.querySelector('#aps-viewer-css')) {
      const link = document.createElement('link');
      link.id = 'aps-viewer-css';
      link.rel = 'stylesheet';
      link.href = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css';
      document.head.appendChild(link);
    }
    // JS インジェクション
    if (!document.querySelector('#aps-viewer-js')) {
      const script = document.createElement('script');
      script.id = 'aps-viewer-js';
      script.src = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js';
      script.onload = () => resolve(window.Autodesk);
      script.onerror = () => {
        apsViewerPromise = null; // 失敗時はリセットして次回リトライ可能にする
        reject(new Error('APS Viewer SDK の読み込みに失敗しました'));
      };
      document.head.appendChild(script);
    } else {
      // スクリプトタグは存在するが onload が既に完了している場合
      if (window.Autodesk) {
        resolve(window.Autodesk);
      } else {
        const existing = document.querySelector('#aps-viewer-js');
        existing.addEventListener('load', () => resolve(window.Autodesk));
        existing.addEventListener('error', () => reject(new Error('APS Viewer SDK の読み込みに失敗しました')));
      }
    }
  });
  return apsViewerPromise;
};

// PDF ベースの 2D (Revit のシート等。子に pdf-page があり f2d が無い) かどうか。
// APS Viewer は PDF を署名 Cookie なしで取りに行くため、CloudFront の Cookie 配信では 403 になり表示できない
const isPdfBased2d = (node) => {
  const children = (node.data && node.data.children) || [];
  const hasPdf = children.some((c) => c.role === 'pdf-page' || c.mime === 'application/pdf');
  const hasF2d = children.some((c) => c.mime === 'application/autodesk-f2d');
  return hasPdf && !hasF2d;
};

// 開くビューを選ぶ。PDF ベースでない 2D (dwg/dxf/dwf の f2d) → 3D → PDF ベースの 2D → 既定の順
const selectGeometryNode = (root) => {
  const nodes2d = root.search({ type: 'geometry', role: '2d' });
  return nodes2d.find((n) => !isPdfBased2d(n))
    || root.search({ type: 'geometry', role: '3d' })[0]
    || nodes2d.find((n) => isPdfBased2d(n))
    || root.getDefaultGeometry();
};

export default {
  name: 'CadViewerAps',
  props: {
    src: { type: String, required: true },
    height: { type: String, default: '500px' },
    shareToken: { type: String, default: null },
    expanded: { type: Boolean, default: false },
  },
  setup(props) {
    const containerRef = ref(null);
    const viewerEl = ref(null);
    const loading = ref(true);
    const error = ref(null);

    let viewerInstance = null;

    const containerStyle = computed(() => ({
      width: '100%',
      height: props.expanded ? '100%' : props.height,
      position: 'relative',
      backgroundColor: 'var(--bg-surface, #f8f9fa)',
    }));

    const viewerStyle = computed(() => ({
      width: '100%',
      height: '100%',
      position: 'relative',
    }));

    const overlayStyle = computed(() => ({
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundColor: 'var(--bg-surface, #f8f9fa)',
      zIndex: '1',
    }));

    const destroyViewer = () => {
      if (viewerInstance) {
        try {
          viewerInstance.finish();
        } catch (_) { /* noop */ }
        viewerInstance = null;
      }
    };

    const initViewer = async (manifestUrl) => {
      destroyViewer();
      loading.value = true;
      error.value = null;

      try {
        const Autodesk = await loadApsViewer();

        if (!viewerEl.value) {
          return;
        }

        // manifest URL の署名クエリ (Expires / Signature / Key-Pair-Id) を除く。
        // APS Viewer は useCredentials 有効時に domain= クエリを追加するため署名 URL と不一致になり 403 になる。
        // manifest.json も derivatives も署名 Cookie で取得する
        const documentUrl = manifestUrl.split('?')[0];

        // manifest URL と同じ CloudFront (output ディストリビューション) を endpoint にする。
        // /derivativeservice/v2/derivatives/{urn} は CloudFront Function が /output/… へ書き換える
        const endpointMatch = documentUrl.match(/^(.*\/exa\/videos\/\d+\/\d+\/cad)\/manifest\.json/);
        const endpointBase = endpointMatch ? endpointMatch[1] : null;

        await new Promise((resolve, reject) => {
            // endpoint が別オリジン (CloudFront) になったため、署名 Cookie を送るよう useCredentials を有効にする。
            // APS Viewer SDK は env が Local のとき既定で Cookie を送らない (useCredentials=false)
            const initOpts = { env: 'Local', useCredentials: true };
            if (endpointBase) {
                initOpts.endpoint = endpointBase;
            }

            Autodesk.Viewing.Initializer(initOpts, () => {
                try {
                    const viewer = new Autodesk.Viewing.GuiViewer3D(viewerEl.value);
                    viewer.start();
                    viewerInstance = viewer;

                    Autodesk.Viewing.Document.load(
                        documentUrl,
                        (doc) => {
                            const root = doc.getRoot();
                            const node = selectGeometryNode(root);

                            if (!node) {
                                reject(new Error('表示可能なジオメトリが見つかりませんでした'));
                                return;
                            }

                            viewer.loadDocumentNode(doc, node)
                                .then(async () => {
                                    loading.value = false;
                                    await nextTick();
                                    viewer.resize();
                                    viewer.fitToView();
                                    resolve();
                                })
                                .catch((e) => {
                                    reject(new Error('CAD モデルの読み込みに失敗しました: ' + e));
                                });
                        },
                        (errorCode) => {
                            reject(new Error('CAD ドキュメントの初期化に失敗しました: ' + errorCode));
                        }
                    );
                } catch (e) {
                    reject(new Error('Viewer の初期化に失敗しました: ' + e.message));
                }
            });
        });
      } catch (e) {
        error.value = e.message || 'CAD ビューアの読み込みに失敗しました';
        loading.value = false;
      }
    };

    onMounted(() => {
      if (props.src) {
        initViewer(props.src);
      }
    });

    onBeforeUnmount(() => {
      destroyViewer();
    });

    watch(() => props.src, (newSrc) => {
      if (newSrc) {
        initViewer(newSrc);
      }
    });

    watch(() => props.expanded, () => {
      if (viewerInstance) {
        setTimeout(() => {
          viewerInstance.resize();
          viewerInstance.fitToView();
        }, 300);
      }
    });

    return {
      containerRef,
      viewerEl,
      loading,
      error,
      containerStyle,
      viewerStyle,
      overlayStyle,
    };
  },
};
</script>
